if ( typeof CartShipping !== 'function' ) {

  /**
   * Renders the real delivery cost in the cart (sidebar + page) and powers the
   * "Not shipping to [country]?" dropdown.
   *
   * Delivery cost is NOT estimated: the standard rate and free-shipping
   * threshold are computed server-side from the "General profile" delivery
   * rules (see snippets/free-shipping-threshold.liquid) and passed in as data
   * attributes.
   */
  class CartShipping extends HTMLElement {

    _message(key, fallback) {
      const messages = window.LabooI18n && window.LabooI18n.shipping;
      return (messages && messages[key]) || fallback;
    }

    _interpolate(template, values) {
      return String(template).replace(/{{\s*(\w+)\s*}}/g, function (_, key) {
        return Object.prototype.hasOwnProperty.call(values, key) ? values[key] : '';
      });
    }

    connectedCallback() {
      this.renderDeliveryPrice();
      this.renderFreeShippingHint();
      this.renderGrandTotal();
      this.initCountryDropdown();
      this.reopenCartIfRequested();
    }

    _shippingIsFree() {
      if ( this._rateMode() !== 'free' ) return false;
      const threshold = Number(this.getAttribute('data-threshold') || 0);
      const cartTotal = Number(this.getAttribute('data-cart-total') || 0);
      return threshold > 0 && cartTotal >= threshold;
    }

    _hasEstimate() {
      return Number(this.getAttribute('data-standard') || 0) > 0;
    }

    _rateMode() {
      return this.getAttribute('data-rate-mode') || 'none';
    }

    _convertRuleAmount(minorUnits) {
      const value = Number(minorUnits) || 0;
      const baseCurrency = (this.getAttribute('data-base-currency') || '').toUpperCase();
      const currency = (this.getAttribute('data-currency') || '').toUpperCase();

      if ( ! baseCurrency || baseCurrency === currency ) return value;

      const shopifyCurrency = window.Shopify && window.Shopify.currency;
      if (
        baseCurrency === 'GBP' &&
        shopifyCurrency &&
        shopifyCurrency.active === currency &&
        Number(shopifyCurrency.rate) > 0
      ) {
        return Math.round(value * Number(shopifyCurrency.rate));
      }

      return null;
    }

    _currentShippingAmount() {
      const standard = this._convertRuleAmount(this.getAttribute('data-standard'));
      if ( standard === null ) return null;

      if ( this._rateMode() === 'reduced' ) {
        const reducedThreshold = this._convertRuleAmount(this.getAttribute('data-reduced-threshold'));
        const reduced = this._convertRuleAmount(this.getAttribute('data-reduced'));
        const cartTotal = Number(this.getAttribute('data-cart-total') || 0);

        if ( reducedThreshold !== null && reduced !== null && cartTotal >= reducedThreshold ) {
          return reduced;
        }
      }

      return standard;
    }

    renderDeliveryPrice() {
      const deliveryEl = this.querySelector('[data-js-delivery-price]');
      const labelEl = this.querySelector('.cart-shipping__label');
      const eligibleEl = this.querySelector('[data-js-delivery-eligible]');

      if ( ! deliveryEl ) return;

      const shippingAmount = this._currentShippingAmount();

      if ( ! this._hasEstimate() || shippingAmount === null ) {
        deliveryEl.textContent = this._message('calculatedAtCheckout', 'Shipping calculated at checkout');
        deliveryEl.classList.remove('cart-shipping__price--free');
        if ( labelEl ) labelEl.hidden = true;
        if ( eligibleEl ) eligibleEl.hidden = true;
        return;
      }

      if ( labelEl ) labelEl.hidden = false;

      if ( this._shippingIsFree() ) {
        deliveryEl.textContent = this._message('freeDelivery', 'FREE Delivery');
        deliveryEl.classList.add('cart-shipping__price--free');
        if ( eligibleEl ) eligibleEl.hidden = true;
      } else {
        deliveryEl.textContent = this._format(shippingAmount, true);
        deliveryEl.classList.remove('cart-shipping__price--free');
        if ( eligibleEl ) eligibleEl.hidden = true;
      }
    }

    renderFreeShippingHint() {
      const threshold = Number(this.getAttribute('data-threshold') || 0);
      const cartTotal = Number(this.getAttribute('data-cart-total') || 0);
      const hintEl = this.querySelector('[data-js-cart-shipping-hint]');

      if ( ! hintEl ) return;

      if ( this._rateMode() !== 'free' || threshold <= 0 ) {
        hintEl.hidden = true;
        hintEl.textContent = '';
        delete this.dataset.freeShippingEligible;
        return;
      }

      const remaining = threshold - cartTotal;

      if ( remaining > 0 ) {
        const amount = this._format(remaining, true);
        hintEl.innerHTML = this._interpolate(
          this._message('spendMoreHtml', 'Spend <strong>{{ amount }}</strong> more to reach free shipping'),
          { amount: amount }
        );
        delete this.dataset.freeShippingEligible;
      } else {
        hintEl.textContent = this._message('qualified', 'You have qualified for FREE delivery');
        this.dataset.freeShippingEligible = 'true';
      }
    }

    renderGrandTotal() {
      const cartTotal = Number(this.getAttribute('data-cart-total') || 0);
      const totalEl = this.querySelector('[data-js-cart-shipping-total]');

      if ( ! totalEl ) return;

      const currentShipping = this._currentShippingAmount();
      const shipping = (this._hasEstimate() && ! this._shippingIsFree() && currentShipping !== null)
        ? currentShipping
        : 0;

      totalEl.textContent = this._format(cartTotal + shipping, true);
    }

    initCountryDropdown() {
      const toggle = this.querySelector('[data-js-cart-shipping-toggle]');
      const dropdown = this.querySelector('[data-js-cart-shipping-dropdown]');
      const select = this.querySelector('[data-js-cart-shipping-select]');
      const form = this.querySelector('form');

      if ( ! toggle || ! dropdown || ! select || ! form ) return;

      toggle.addEventListener('click', function () {
        const market = document.querySelector('[data-laboo-market-selector]');
        if ( market && market.getAttribute('data-laboo-market-ready') === 'true' && market.querySelector('#laboo-market-modal') ) {
          market.dispatchEvent(new CustomEvent('laboo:open-market-selector', {
            detail: { opener: toggle, fromCart: true }
          }));
          return;
        }
        const willOpen = dropdown.hidden;
        dropdown.hidden = ! willOpen;
        toggle.setAttribute('aria-expanded', String(willOpen));
        if ( willOpen ) {
          select.focus({ preventScroll: true });
        }
      });

      select.addEventListener('change', function () {
        if ( select.value ) {
          try { sessionStorage.setItem('laboo_reopen_cart', '1'); } catch ( e ) {}
          form.submit();
        }
      });

      document.addEventListener('click', function (event) {
        if ( ! event.target.closest('cart-shipping') && ! dropdown.hidden ) {
          dropdown.hidden = true;
          toggle.setAttribute('aria-expanded', 'false');
        }
      });
    }

    reopenCartIfRequested() {
      let shouldReopen = false;
      try {
        shouldReopen = sessionStorage.getItem('laboo_reopen_cart') === '1';
        if ( shouldReopen ) sessionStorage.removeItem('laboo_reopen_cart');
      } catch ( e ) {}

      if ( ! shouldReopen ) return;

      const sidebar = document.getElementById('site-cart-sidebar');
      if ( ! sidebar ) return;

      const tryShow = () => {
        if ( typeof sidebar.show === 'function' ) {
          sidebar.show();
        } else {
          requestAnimationFrame(tryShow);
        }
      };

      tryShow();
    }

    _localeFor(currency) {
      switch ( ( currency || '' ).toUpperCase() ) {
        case 'EUR': return 'de-DE';
        case 'SEK': return 'sv-SE';
        case 'DKK': return 'da-DK';
        case 'CZK': return 'cs-CZ';
        case 'HUF': return 'hu-HU';
        case 'PLN': return 'pl-PL';
        case 'RON': return 'ro-RO';
        case 'CHF': return 'de-CH';
        case 'GBP': return 'en-GB';
        case 'USD': return 'en-US';
        case 'AUD': return 'en-AU';
        case 'CAD': return 'en-CA';
        case 'NZD': return 'en-NZ';
        case 'SGD': return 'en-SG';
        case 'AED': return 'en-AE';
        default: return this.getAttribute('data-locale') || 'en';
      }
    }

    _format(cents, fixed) {
      const currency = this.getAttribute('data-currency') || 'EUR';
      const value = Number(cents) || 0;
      const minimumFractionDigits = fixed ? 2 : 0;

      try {
        return new Intl.NumberFormat(this._localeFor(currency), {
          style: 'currency',
          currency: currency,
          minimumFractionDigits: minimumFractionDigits,
          maximumFractionDigits: 2
        }).format(value / 100);
      } catch ( e ) {
        return new Intl.NumberFormat('en', {
          style: 'currency',
          currency: 'EUR',
          minimumFractionDigits: minimumFractionDigits,
          maximumFractionDigits: 2
        }).format(value / 100);
      }
    }

  }

  if ( typeof customElements.get('cart-shipping') == 'undefined' ) {
    customElements.define('cart-shipping', CartShipping);
  }

}
